import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import JobCard from '../../components/JobCard';
import Seo from '../../components/Seo';
import { Button } from '../../components/ui/button';
import { Loader2, ArrowRight, Search, CheckCircle2, Compass } from 'lucide-react';

// One reusable, config-driven template powering many SEO landing pages.
// Every entry has unique title / meta / H1 / intro / body so no page is a thin doorway.
const REL = {
  lmsw: [
    { label: 'LMSW License Requirements', to: '/license-renewal-center' },
    { label: 'ASWB Exam Prep', to: '/license-renewal-center' },
    { label: 'Find a Supervisor', to: '/find-a-supervisor' },
    { label: 'Remote Social Work Jobs', to: '/remote-social-work-jobs' },
  ],
  lcsw: [
    { label: 'LCSW License & Renewal', to: '/license-renewal-center' },
    { label: 'Clinical Supervisor Directory', to: '/find-a-supervisor' },
    { label: 'Behavioral Health Jobs', to: '/behavioral-health-social-worker-jobs' },
    { label: 'ASWB Clinical Exam Prep', to: '/license-renewal-center' },
  ],
  resources: [
    { label: 'Community Resource Directory', to: '/community/resources' },
    { label: 'Social Work Forms Center', to: '/social-work-forms' },
    { label: 'Advance Directives & Legal Forms', to: '/advance-directives' },
    { label: 'Find an Internship', to: '/internships' },
  ],
};

export const JOB_LANDINGS = {
  // ---------------- License / keyword job pages ----------------
  'lmsw-jobs': {
    metaTitle: 'LMSW Jobs Nationwide | Master Social Worker Openings | INASW',
    metaDesc: 'Browse current LMSW (Licensed Master Social Worker) jobs nationwide — hospital, behavioral health, school, medical and remote roles on a platform built only for social work.',
    kicker: 'By License', h1: 'LMSW Jobs Nationwide',
    intro: 'Current Licensed Master Social Worker (LMSW) openings across all 50 states — hospital, behavioral health, medical, school and remote roles. Every listing here is a genuine social work position.',
    params: { license: 'LMSW' },
    about: 'LMSWs practice in medical centers, community mental health, schools, hospice and government agencies. Filter by location, setting and remote status, then connect the dots between your job search, your LMSW license renewal, ASWB exam prep, and finding a clinical supervisor if you are working toward your LCSW.',
    related: REL.lmsw,
  },
  'lcsw-jobs': {
    metaTitle: 'LCSW Jobs Nationwide | Clinical Social Worker Openings | INASW',
    metaDesc: 'Find LCSW (Licensed Clinical Social Worker) jobs nationwide — therapy, behavioral health, private practice, telehealth and supervisory roles on a social-work-only platform.',
    kicker: 'By License', h1: 'LCSW Jobs Nationwide',
    intro: 'Current Licensed Clinical Social Worker (LCSW) openings nationwide — clinical, behavioral health, telehealth, private practice and supervisory roles built specifically for social work professionals.',
    params: { license: 'LCSW' },
    about: 'LCSWs provide clinical therapy and diagnosis and often supervise associate-level clinicians. Pair your job search with LCSW renewal requirements, clinical exam prep and our clinical supervisor directory.',
    related: REL.lcsw,
  },
  'lbsw-jobs': {
    metaTitle: 'LBSW Jobs Nationwide | Bachelor Social Worker Openings | INASW',
    metaDesc: 'Browse LBSW (Licensed Bachelor Social Worker) jobs nationwide — case management, community services, child welfare and care coordination roles.',
    kicker: 'By License', h1: 'LBSW Jobs Nationwide',
    intro: 'Entry and early-career Licensed Bachelor Social Worker (LBSW) roles nationwide — case management, community services, child welfare and care coordination.',
    params: { license: 'LBSW' },
    about: 'LBSW roles are a strong first step in a social work career. Explore case-management and community positions, then plan ahead with license information and supervision options as you grow toward an LMSW or LCSW.',
    related: REL.lmsw,
  },
  'remote-social-work-jobs': {
    metaTitle: 'Remote Social Work Jobs Nationwide | Telehealth & WFH | INASW',
    metaDesc: 'Find remote and telehealth social work jobs nationwide — remote LCSW therapy, remote case management, telehealth behavioral health and work-from-home social work roles.',
    kicker: 'Work Style', h1: 'Remote Social Work Jobs',
    intro: 'Fully remote and telehealth social work opportunities nationwide — remote therapy, virtual case management and work-from-home behavioral health roles.',
    params: { is_remote: 'true' },
    about: 'Remote social work has grown fast, especially in telehealth therapy and care coordination. Many remote clinical roles require an LCSW; use our licensing center to confirm what your state and employer require before you apply.',
    related: REL.lcsw,
  },
  'medical-social-worker-jobs': {
    metaTitle: 'Medical Social Worker Jobs Nationwide | Hospital & Healthcare | INASW',
    metaDesc: 'Browse medical social worker jobs nationwide — hospital, discharge planning, case management, dialysis and healthcare social work roles.',
    kicker: 'By Specialty', h1: 'Medical Social Worker Jobs',
    intro: 'Hospital and healthcare social work roles nationwide — discharge planning, care coordination, dialysis, oncology and emergency-department social work.',
    params: { search: 'medical social worker' },
    about: 'Medical social workers help patients and families navigate diagnosis, discharge and community support. These roles connect naturally to advance directives, hospital resources and case-management tools.',
    related: REL.resources,
  },
  'hospice-social-worker-jobs': {
    metaTitle: 'Hospice Social Worker Jobs Nationwide | Palliative & End-of-Life | INASW',
    metaDesc: 'Find hospice and palliative care social worker jobs nationwide — end-of-life support, grief counseling and interdisciplinary hospice team roles.',
    kicker: 'By Specialty', h1: 'Hospice Social Worker Jobs',
    intro: 'Hospice and palliative care social work roles nationwide — end-of-life support, grief and bereavement, and interdisciplinary team positions.',
    params: { search: 'hospice' },
    about: 'Hospice social workers support patients and families through end-of-life planning. Pair these roles with our advance directives library and community grief resources.',
    related: REL.resources,
  },
  'school-social-worker-jobs': {
    metaTitle: 'School Social Worker Jobs Nationwide | K-12 & Student Support | INASW',
    metaDesc: 'Browse school social worker jobs nationwide — K-12 student support, IEP teams, crisis intervention and family engagement roles.',
    kicker: 'By Specialty', h1: 'School Social Worker Jobs',
    intro: 'K-12 school social work roles nationwide — student support, IEP and special-education teams, crisis intervention and family engagement.',
    params: { search: 'school' },
    about: 'School social workers bridge home, school and community. These roles connect to our parent resource center, school forms and internship portal for students entering the field.',
    related: REL.resources,
  },
  'behavioral-health-social-worker-jobs': {
    metaTitle: 'Behavioral Health Social Worker Jobs Nationwide | Mental Health | INASW',
    metaDesc: 'Find behavioral health and mental health social worker jobs nationwide — therapy, substance use, crisis and community mental health roles.',
    kicker: 'By Specialty', h1: 'Behavioral Health Social Worker Jobs',
    intro: 'Behavioral and mental health social work roles nationwide — therapy, substance use treatment, crisis services and community mental health.',
    params: { search: 'behavioral health' },
    about: 'Behavioral health is one of the largest fields for social workers. Many clinical roles require an LCSW; explore our behavioral health assessments and licensing center alongside your search.',
    related: REL.lcsw,
  },
  'prn-social-worker-jobs': {
    metaTitle: 'PRN Social Worker Jobs Nationwide | Per Diem & As-Needed | INASW',
    metaDesc: 'Browse PRN (per diem / as-needed) social worker jobs nationwide — flexible hospital, hospice and behavioral health shifts.',
    kicker: 'Work Style', h1: 'PRN Social Worker Jobs',
    intro: 'Per diem and as-needed (PRN) social work roles nationwide — flexible hospital, hospice and behavioral health shifts that fit around your schedule.',
    params: { search: 'PRN' },
    about: 'PRN roles offer flexibility and variety. They are common in medical and hospice settings — pair your search with license renewal reminders so your credentials stay current between assignments.',
    related: REL.lmsw,
  },
  'clinical-social-worker-jobs': {
    metaTitle: 'Clinical Social Worker Jobs Nationwide | Therapy & Diagnosis | INASW',
    metaDesc: 'Find clinical social worker jobs nationwide — psychotherapy, diagnosis, private practice and telehealth roles, most requiring an LCSW.',
    kicker: 'By Specialty', h1: 'Clinical Social Worker Jobs',
    intro: 'Clinical social work roles nationwide — psychotherapy, assessment and diagnosis, private practice and telehealth positions.',
    params: { search: 'clinical' },
    about: 'Clinical roles typically require an LCSW. Combine your search with clinical exam prep, the clinical supervisor directory and LCSW renewal information.',
    related: REL.lcsw,
  },
  // ---------------- Core hub + additional specialty pages ----------------
  'social-work-jobs': {
    metaTitle: 'Social Work Jobs Nationwide | LCSW, LMSW & LBSW Careers | INASW',
    metaDesc: 'Browse social work jobs nationwide on the platform built only for social workers — LCSW, LMSW, LBSW, hospital, hospice, school, medical, behavioral health and remote roles.',
    kicker: 'All Jobs', h1: 'Social Work Jobs Nationwide',
    intro: 'Every open social work role on I Need A Social Worker — across all 50 states and every setting. No unrelated listings, no clutter: just genuine social work positions for LCSW, LMSW, LBSW and social work assistants.',
    params: {},
    about: 'Search by license, specialty, location or remote status. Pair your search with license renewal reminders, ASWB exam prep, clinical supervision and our community resource directory — everything a social worker needs in one place.',
    related: REL.lcsw,
  },
  'mental-health-social-worker-jobs': {
    metaTitle: 'Mental Health Social Worker Jobs Nationwide | INASW',
    metaDesc: 'Find mental health social worker jobs nationwide — therapy, counseling, community mental health, crisis and psychiatric support roles built for social workers.',
    kicker: 'By Specialty', h1: 'Mental Health Social Worker Jobs',
    intro: 'Mental health social work roles nationwide — outpatient therapy, community mental health, crisis response and integrated behavioral health.',
    params: { search: 'mental health' },
    about: 'Mental health is a core field of social work practice. Many therapy roles require an LCSW — combine your search with clinical exam prep, supervision and LCSW renewal resources.',
    related: REL.lcsw,
  },
  'dialysis-social-worker-jobs': {
    metaTitle: 'Dialysis Social Worker Jobs Nationwide | Nephrology | INASW',
    metaDesc: 'Browse dialysis (nephrology) social worker jobs nationwide — outpatient dialysis center and renal-care social work roles for LMSW and LCSW professionals.',
    kicker: 'By Specialty', h1: 'Dialysis Social Worker Jobs',
    intro: 'Dialysis and nephrology social work roles nationwide — supporting patients with chronic kidney disease across outpatient dialysis centers and hospital renal units.',
    params: { search: 'dialysis' },
    about: 'Dialysis social workers help patients manage treatment, benefits and psychosocial needs. Most roles accept an LMSW or LCSW — keep your license current with our renewal center.',
    related: REL.lmsw,
  },
  'case-management-social-worker-jobs': {
    metaTitle: 'Case Management Social Worker Jobs Nationwide | INASW',
    metaDesc: 'Find case management social worker jobs nationwide — hospital discharge planning, community care coordination and medical case management roles.',
    kicker: 'By Specialty', h1: 'Case Management Social Worker Jobs',
    intro: 'Case management social work roles nationwide — hospital discharge planning, care coordination, and community-based case management.',
    params: { search: 'case manage' },
    about: 'Case management spans hospitals, managed care, and community agencies. Roles often accept an LMSW or LBSW — explore licensing and supervision resources alongside your search.',
    related: REL.lmsw,
  },
  'correctional-social-worker-jobs': {
    metaTitle: 'Correctional Social Worker Jobs Nationwide | Justice-Involved | INASW',
    metaDesc: 'Browse correctional and forensic social worker jobs nationwide — jail, prison, reentry, probation and justice-involved social work roles.',
    kicker: 'By Specialty', h1: 'Correctional Social Worker Jobs',
    intro: 'Correctional and justice-involved social work roles nationwide — jails, prisons, reentry programs, probation and diversion services.',
    params: { search: 'correctional' },
    about: 'Correctional social work supports incarcerated and reentering individuals. Related forensic roles are common — see our forensic social work jobs and licensing resources.',
    related: REL.lcsw,
  },
  'psychiatric-social-worker-jobs': {
    metaTitle: 'Psychiatric Social Worker Jobs Nationwide | Inpatient & Crisis | INASW',
    metaDesc: 'Find psychiatric social worker jobs nationwide — inpatient psychiatric units, crisis stabilization, and psychiatric emergency services roles.',
    kicker: 'By Specialty', h1: 'Psychiatric Social Worker Jobs',
    intro: 'Psychiatric social work roles nationwide — inpatient psychiatric units, crisis stabilization, psychiatric emergency services and integrated care.',
    params: { search: 'psychiatric' },
    about: 'Psychiatric social workers support patients with acute mental health needs. Many roles require an LCSW — pair your search with clinical exam prep and supervision.',
    related: REL.lcsw,
  },
  // ---------------- Job-board comparison hub ----------------
  'social-work-job-boards': {
    variant: 'comparison',
    metaTitle: 'Best Social Work Job Boards: Indeed, ZipRecruiter & INASW',
    metaDesc: 'Compare the best places to find social work jobs online — Indeed, ZipRecruiter, LinkedIn, government and hospital sites, and INASW, a platform built specifically for social work careers.',
    kicker: 'Guide', h1: 'Best Places to Find Social Work Jobs Online',
    intro: 'Social workers search a lot of places for their next role. Here is an honest look at where those jobs live — and why a social-work-specific platform can save you time.',
    params: {},
    related: REL.resources,
  },
  // ---------------- Competitor-intent pages (Indeed) ----------------
  'indeed-social-work-jobs': {
    variant: 'competitor', competitor: 'Indeed',
    metaTitle: 'Indeed Social Work Jobs & Social Work Job Board Alternatives | INASW',
    metaDesc: 'Searching Indeed for social work jobs? Compare your options and browse social-work-specific openings, licensing info and career tools on INASW.',
    kicker: 'Compare Your Options', h1: 'Searching Indeed for Social Work Jobs? Compare Your Options',
    intro: 'Indeed is a large general job board serving every industry. INASW is built specifically around social work careers, licensing, supervision and professional tools.',
    params: {}, cta: 'Browse Social Work Jobs', related: REL.resources,
  },
  'indeed-lmsw-jobs': {
    variant: 'competitor', competitor: 'Indeed',
    metaTitle: 'Indeed LMSW Jobs & Social-Work-Specific LMSW Openings | INASW',
    metaDesc: 'Searching Indeed for LMSW jobs? Browse Licensed Master Social Worker openings plus LMSW licensing and exam-prep resources on INASW.',
    kicker: 'Compare Your Options', h1: 'Searching Indeed for LMSW Jobs?',
    intro: 'General job boards like Indeed list LMSW roles among millions of unrelated postings. INASW shows only social work jobs and pairs them with LMSW licensing and supervision resources.',
    params: { license: 'LMSW' }, cta: 'View LMSW Jobs', related: REL.lmsw,
  },
  'indeed-lcsw-jobs': {
    variant: 'competitor', competitor: 'Indeed',
    metaTitle: 'Indeed LCSW Jobs & Social-Work-Specific LCSW Openings | INASW',
    metaDesc: 'Searching Indeed for LCSW jobs? Browse Licensed Clinical Social Worker openings plus clinical licensing and supervisor resources on INASW.',
    kicker: 'Compare Your Options', h1: 'Searching Indeed for LCSW Jobs?',
    intro: 'Indeed serves every industry. INASW focuses only on social work — including LCSW clinical roles, exam prep and the clinical supervisor directory.',
    params: { license: 'LCSW' }, cta: 'View LCSW Jobs', related: REL.lcsw,
  },
  'indeed-remote-social-work-jobs': {
    variant: 'competitor', competitor: 'Indeed',
    metaTitle: 'Indeed Remote Social Work Jobs & Telehealth Alternatives | INASW',
    metaDesc: 'Searching Indeed for remote social work jobs? Browse remote and telehealth social work openings on a platform built specifically for the profession.',
    kicker: 'Compare Your Options', h1: 'Searching Indeed for Remote Social Work Jobs?',
    intro: 'Remote roles can be hard to filter on a general board. INASW lets you see remote and telehealth social work jobs directly, with licensing guidance for clinical telehealth.',
    params: { is_remote: 'true' }, cta: 'View Remote Jobs', related: REL.lcsw,
  },
  // ---------------- Competitor-intent pages (ZipRecruiter) ----------------
  'ziprecruiter-social-work-jobs': {
    variant: 'competitor', competitor: 'ZipRecruiter',
    metaTitle: 'ZipRecruiter Social Work Jobs & Social Work Job Board Alternatives | INASW',
    metaDesc: 'Searching ZipRecruiter for social work jobs? See social-work-specific openings, licensing info and career tools on INASW.',
    kicker: 'Compare Your Options', h1: 'Searching ZipRecruiter for Social Work Jobs?',
    intro: 'ZipRecruiter is a broad job marketplace across many industries. INASW is designed specifically around social work careers, licensing, resources and supervision.',
    params: {}, cta: 'Search INASW Jobs', related: REL.resources,
  },
  'ziprecruiter-lmsw-jobs': {
    variant: 'competitor', competitor: 'ZipRecruiter',
    metaTitle: 'ZipRecruiter LMSW Jobs & Social-Work-Specific LMSW Openings | INASW',
    metaDesc: 'Searching ZipRecruiter for LMSW jobs? Browse LMSW openings plus licensing and exam-prep resources on a social-work-only platform.',
    kicker: 'Compare Your Options', h1: 'Searching ZipRecruiter for LMSW Jobs?',
    intro: 'ZipRecruiter aggregates jobs across all fields. INASW shows only social work roles — including LMSW positions with licensing and supervision resources.',
    params: { license: 'LMSW' }, cta: 'View LMSW Jobs', related: REL.lmsw,
  },
  'ziprecruiter-lcsw-jobs': {
    variant: 'competitor', competitor: 'ZipRecruiter',
    metaTitle: 'ZipRecruiter LCSW Jobs & Social-Work-Specific LCSW Openings | INASW',
    metaDesc: 'Searching ZipRecruiter for LCSW jobs? Browse LCSW clinical openings plus licensing and clinical supervisor resources on INASW.',
    kicker: 'Compare Your Options', h1: 'Searching ZipRecruiter for LCSW Jobs?',
    intro: 'ZipRecruiter is a general marketplace. INASW focuses only on social work — including LCSW clinical roles, exam prep and supervisor listings.',
    params: { license: 'LCSW' }, cta: 'View LCSW Jobs', related: REL.lcsw,
  },
  'ziprecruiter-remote-social-work-jobs': {
    variant: 'competitor', competitor: 'ZipRecruiter',
    metaTitle: 'ZipRecruiter Remote Social Work Jobs & Telehealth Alternatives | INASW',
    metaDesc: 'Searching ZipRecruiter for remote social work jobs? Browse remote and telehealth social work openings on a platform built for the profession.',
    kicker: 'Compare Your Options', h1: 'Searching ZipRecruiter for Remote Social Work Jobs?',
    intro: 'Filtering remote social work roles on a broad board is tough. INASW shows remote and telehealth social work jobs directly, with clinical licensing guidance.',
    params: { is_remote: 'true' }, cta: 'View Remote Jobs', related: REL.lcsw,
  },
};

const BOARDS = [
  ['Indeed', 'Huge general job board across all industries. High volume, but you sift through unrelated roles and there are no social-work-specific tools.'],
  ['ZipRecruiter', 'Broad marketplace that distributes your search widely. Convenient, but not tailored to social work licensing or specialties.'],
  ['LinkedIn', 'Great for networking and some roles, though social work openings compete with every other profession.'],
  ['Government sites (USAJOBS, state, county)', 'Excellent for public-sector roles but fragmented across many separate portals.'],
  ['Hospital & school career sites', 'Direct source for medical and school social work, but you must check each employer individually.'],
  ['INeedASocialWorker.com', 'Built only for social work — license-specific search (LBSW, LMSW, LCSW), specialties, remote/PRN, plus licensing, supervisors, internships, resources and forms in one place.'],
];

const CTA_BUTTONS = [
  ['Browse Social Work Jobs', '/jobs'],
  ['Browse LMSW Jobs', '/lmsw-jobs'],
  ['Browse LCSW Jobs', '/lcsw-jobs'],
  ['Remote Social Work Jobs', '/remote-social-work-jobs'],
];

const JobLandingPage = ({ landingKey }) => {
  const cfg = JOB_LANDINGS[landingKey];
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '24' });
      if (cfg.params.license) p.append('license_type', cfg.params.license);
      if (cfg.params.search) p.append('search', cfg.params.search);
      if (cfg.params.is_remote) p.append('is_remote', 'true');
      const r = await axios.get(`${API_URL}/jobs?${p.toString()}`);
      setJobs(r.data.jobs || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [cfg]);
  useEffect(() => { load(); }, [load]);

  const jobsHref = () => {
    const p = new URLSearchParams();
    if (cfg.params.license) p.set('license', cfg.params.license);
    if (cfg.params.search) p.set('search', cfg.params.search);
    if (cfg.params.is_remote) p.set('is_remote', 'true');
    const q = p.toString();
    return q ? `/jobs?${q}` : '/jobs';
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid={`job-landing-${landingKey}`}>
      <Seo exactTitle={cfg.metaTitle} description={cfg.metaDesc} path={`/${landingKey}`} />
      <section className="bg-primary text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-secondary font-semibold text-sm mb-3">
            <Compass className="w-5 h-5" /> {cfg.kicker}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold leading-tight max-w-3xl">{cfg.h1}</h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl">{cfg.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={jobsHref()} data-testid="landing-primary-cta">
              <Button className="bg-secondary hover:bg-secondary-hover text-white h-11">
                {cfg.cta || 'Browse matching jobs'} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {cfg.variant === 'comparison' && (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8 mb-8" data-testid="board-comparison">
            <h2 className="font-serif text-2xl font-bold text-primary mb-4">Where social workers look for jobs</h2>
            <div className="space-y-3">
              {BOARDS.map(([name, desc]) => (
                <div key={name} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm"><span className="font-semibold text-primary">{name}:</span> {desc}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-6">
              {CTA_BUTTONS.map(([label, to]) => (
                <Link key={to} to={to}><Button variant="outline" className="h-10">{label}</Button></Link>
              ))}
            </div>
          </div>
        )}

        {cfg.variant === 'competitor' && (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8 mb-8" data-testid="competitor-note">
            <p className="text-gray-700">
              {cfg.competitor} serves many industries and is a fine place to start a broad search.
              INeedASocialWorker.com is different: it is built specifically for social work, so every
              result is a real social work role and you get license-specific search, specialties, and
              career tools alongside it. We are not affiliated with {cfg.competitor}.
            </p>
            <Link to={jobsHref()} className="inline-block mt-4">
              <Button className="bg-primary hover:bg-primary-hover text-white">{cfg.cta || 'Browse Jobs'} <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </Link>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl font-bold text-primary">Current openings</h2>
          <p className="text-sm text-gray-500" data-testid="landing-jobs-count">{loading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'job' : 'jobs'}`}</p>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : jobs.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center" data-testid="landing-empty">
            <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <h3 className="font-serif text-xl font-bold text-primary mb-2">No matching roles right now</h3>
            <p className="text-gray-500 max-w-md mx-auto text-sm mb-4">New roles are added continuously. Set a job alert, or explore related searches below.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {(cfg.related || REL.resources).map((r) => (
                <Link key={r.to} to={r.to}><Button variant="outline" size="sm">{r.label}</Button></Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {jobs.map((job) => (<JobCard key={job.job_id} job={job} />))}
          </div>
        )}

        {cfg.about && (
          <div className="mt-10 bg-white border border-gray-100 rounded-2xl p-6 md:p-8">
            <h2 className="font-serif text-2xl font-bold text-primary mb-2">More about these roles</h2>
            <p className="text-gray-600 text-sm">{cfg.about}</p>
          </div>
        )}

        <div className="mt-8" data-testid="landing-related">
          <h3 className="font-semibold text-primary mb-3">Related on INASW</h3>
          <div className="flex flex-wrap gap-2">
            {(cfg.related || REL.resources).map((r) => (
              <Link key={r.to} to={r.to}><Button variant="outline" size="sm">{r.label}</Button></Link>
            ))}
            <Link to="/social-work-job-boards"><Button variant="outline" size="sm">Compare Social Work Job Boards</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobLandingPage;
